import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Star, Calendar, ArrowLeft, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { MentorTierBadge } from "@/components/MentorTierBadge";
import { NotificationBell } from "@/components/NotificationBell";
import { useMentors } from "@/hooks/useMentors";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import TruncatedCredits from "@/components/TruncatedCredits";
import { safeSetItem } from "@/lib/safe-storage";
import ComplianceFooter from "@/components/ComplianceFooter";

const defaultAvatar = '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png';

const AllMentors = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { mentors: allMentors, loading, error } = useMentors();
  
  // Filter out mentors without profile pictures and apply search
  const mentors = allMentors.filter(mentor => {
    if (!mentor.profile_image_url || mentor.profile_image_url.trim() === '') return false;
    
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const fullName = `${mentor.first_name} ${mentor.last_name}`.toLowerCase();
    const specialty = mentor.specialty?.toLowerCase() || '';
    const expertise = mentor.expertise?.join(' ').toLowerCase() || '';
    const credits = mentor.credits?.join(' ').toLowerCase() || '';
    
    return fullName.includes(query) || 
           specialty.includes(query) || 
           expertise.includes(query) ||
           credits.includes(query);
  });


  // Check for current user on component mount
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
        setCurrentUser({ ...session.user, profile });
      }
    };
    checkUser();
  }, []);

  const handleBookMentor = async (mentorId: string) => {
    try {
      // Step 1: Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Store mentor ID safely to return after login
        safeSetItem('pendingMentorBooking', mentorId);
        toast({
          title: "Login Required",
          description: "Please log in to book a session with this mentor.",
        });
        navigate('/auth/student');
        return;
      }

      // Step 2: Check if user is a student (role validation)
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();

      if (profile?.role !== 'student' && profile?.role !== 'admin') {
        toast({
          title: "Access Denied",
          description: "Only students can book mentor sessions.",
          variant: "destructive",
        });
        return;
      }

      // Step 3: Navigate to student dashboard with mentor info
      safeSetItem('selectedMentorId', mentorId);
      toast({
        title: "Redirecting...",
        description: "Taking you to book your session.",
      });
      navigate('/dashboard/student');
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };


  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Header */}
      <header className="border-b border-border obsidian-glass">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img 
              src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
              alt="Ascendry" 
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-4">
            {currentUser && <NotificationBell />}
            <Button variant="outline" asChild>
              {currentUser?.profile?.role === 'student' ? (
                <Link to="/dashboard/student">
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Dashboard
                </Link>
              ) : (
                <Link to="/">
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Home
                </Link>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-glow">All Mentors</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Browse our complete roster of expert mentors and find the perfect match for your learning goals
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5 z-10" />
            <Input
              type="text"
              placeholder="Search mentors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-24 md:pr-20 py-3 text-base md:text-lg bg-card/50 border-border/50 rounded-full focus:bg-card/80 transition-all duration-200"
            />
            <Button 
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 rounded-full px-3 md:px-4 text-xs md:text-sm z-10"
              onClick={() => {}}
            >
              Search
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <h3 className="text-2xl font-bold mb-4 text-destructive">Error Loading Mentors</h3>
            <p className="text-xl text-muted-foreground">{error}</p>
          </div>
        ) : mentors.length === 0 ? (
          <div className="text-center py-20">
            <h3 className="text-2xl font-bold mb-4 text-glow">No Mentors Available</h3>
            <p className="text-xl text-muted-foreground">New mentors are joining soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mentors.map((mentor) => (
              <Card 
                key={mentor.id} 
                className="w-full min-h-[620px] obsidian-card flex flex-col hover:scale-105 transition-transform duration-300 cursor-pointer group"
                onClick={() => navigate(`/mentor/${mentor.id}`)}
              >
                <CardContent className="p-6 flex flex-col items-center h-full">
                  {/* Mentor Image */}
                  <div className="mb-6">
                    <div className="relative">
                      <div className="w-40 h-40 rounded-lg overflow-hidden bg-muted">
                        <img
                          src={mentor.profile_image_url || defaultAvatar}
                          alt={`${mentor.first_name} ${mentor.last_name}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.currentTarget.src = defaultAvatar;
                          }}
                        />
                        {mentor.discovery_call_enabled && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent py-1.5 text-center">
                            <span className="text-xs font-bold text-primary-foreground tracking-wide">
                              FREE CONSULT
                            </span>
                          </div>
                        )}
                      </div>
                      <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} className="absolute -bottom-1 -right-1" />
                    </div>
                  </div>

                  {/* Mentor Info */}
                  <div className="text-center mb-4">
                    <h4 className="font-bold text-lg text-energy mb-1">
                      {mentor.first_name} {mentor.last_name}
                    </h4>
                    <p
                      className="text-sm text-muted-foreground mb-1 min-h-[40px]"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {mentor.specialty}
                    </p>
                    
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-medium">{mentor.rating.toFixed(1)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">({mentor.total_sessions})</span>
                    </div>
                  </div>

                  {/* Credits Section */}
                  <div className="flex-1 flex flex-col w-full">
                    <div className="h-24 mb-4">
                      <h5 className="text-sm font-semibold text-muted-foreground mb-2">Credits & Accolades</h5>
                      {mentor.credits && mentor.credits.length > 0 ? (
                        <TruncatedCredits 
                          credits={mentor.credits} 
                          mentorId={mentor.id} 
                          maxLines={4} 
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground/60">No credits listed</p>
                      )}
                    </div>
                  </div>

                  {/* Rate and Buttons */}
                  <div className="mt-auto pt-4 border-t border-border w-full">
                    <div className="text-center mb-3">
                      <div>
                        <span className="text-lg font-bold text-destructive">
                          ${(mentor.hourly_rate / 100).toFixed(0)}/hr
                        </span>
                        <span className="text-sm text-muted-foreground block">
                          {(mentor.hourly_rate / 100).toFixed(0)} Tokens/hr
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 w-full">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 text-sm min-w-0" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/mentor/${mentor.id}`);
                        }}
                      >
                        Profile
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1 energy-button text-sm min-w-0" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBookMentor(mentor.id);
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Book
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default AllMentors;